import {
  BACKEND_API,
  type ProductFilter,
  SearchProductEntity,
} from '@trwl/backend.api';
import { $filter } from '@trwl/store/filter';
import { createEffect, createEvent, createStore, sample } from 'effector';
import { debounce } from 'patronum/debounce';

export const clearProducts = createEvent();
export const initProducts = createEvent();
export const setCatalogPage = createEvent<number>();
export const reloadProductById = createEvent<number>();
export const findProductsV2 = createEvent<ProductFilter>();
const findProductsV2Debounced = debounce(findProductsV2, 50);
const reloadProductByIdDebounced = debounce(reloadProductById, 300);

export const nextCatalogPage = createEvent();
export const catalogPage = createStore<number>(0)
  .on(setCatalogPage, (_, page) => page)
  .on(nextCatalogPage, (page) => page + 1)
  .reset(clearProducts);
let currentAbortController: AbortController | null = null;

export const findProductsV2Fx = createEffect<
  ProductFilter,
  SearchProductEntity[],
  Error
>(async (productFilter: ProductFilter) => {
  if (currentAbortController) {
    currentAbortController.abort();
  }
  currentAbortController = new AbortController();
  const { signal } = currentAbortController;
  const promise = async () => {
    if (
      (productFilter.seriesIds && productFilter.seriesIds?.length > 0) ||
      (productFilter.sortField &&
        productFilter.sizes &&
        productFilter.sizes?.length > 0)
    )
      return BACKEND_API.ElasticApi.findProducts(productFilter, {
        signal,
      }).then((res) => res.data.content ?? []);
    return BACKEND_API.ElasticApi.findProductsV2(productFilter, {
      signal,
    }).then((res) => res.data);
  };

  return promise().then((data) => {
    const result = new Array<SearchProductEntity>();
    data.forEach((product) => {
      if (product.content) result.push(product.content);
    });
    return result;
  });
});

export const reloadProductByIdFx = createEffect<
  number,
  SearchProductEntity,
  Error
>(async (productId) => {
  const { data } = await BACKEND_API.ElasticApi.findProductsV2({
    productIds: [productId],
    page: 0,
    size: 5,
  });
  const found = data.find(
    (item: any) => item.content && item.content.id === productId,
  );
  if (!found || !found.content) throw new Error('Product not found');
  return found.content;
});

export const findProductsV2Loading = createStore<boolean>(false).on(
  findProductsV2Fx.pending,
  (_, pending) => pending,
);

export const catalogProducts = createStore<SearchProductEntity[]>([])
  .on(findProductsV2Fx.doneData, (state, products) => {
    const productsMap = new Map(state.map((t) => [t.id, t]));
    const result = [...state];
    products.forEach((product) => {
      const oldProduct = productsMap.get(product.id);
      if (oldProduct) {
        if (oldProduct.updatedAt !== product.updatedAt) {
          const index = result.findIndex((item) => item.id === product.id);
          if (index !== -1) {
            result[index] = product;
          }
        }
      } else {
        result.push(product);
      }
    });
    return result;
  })
  .on(reloadProductByIdFx.doneData, (state, updatedProduct) => {
    const idx = state.findIndex((p) => p.id === updatedProduct.id);
    if (idx === -1) return state;
    const newState = [...state];
    newState[idx] = updatedProduct;
    return newState;
  })
  .reset(clearProducts);

export const findProductsHasNext = createStore<boolean>(true).reset(
  findProductsV2,
  clearProducts,
);

sample({
  source: $filter,
  clock: $filter.updates,
  target: clearProducts,
});

sample({
  source: $filter,
  clock: findProductsV2Fx.doneData,
  fn: (filter, products) => products.length >= filter.size,
  target: findProductsHasNext,
});

sample({
  source: { filter: $filter, page: catalogPage },
  clock: [catalogPage.updates, $filter.updates, initProducts],
  fn: ({ filter, page }) => ({
    ...filter,
    page,
    stock: true,
  }),
  target: findProductsV2,
});

findProductsV2Debounced.watch(findProductsV2Fx);
reloadProductByIdDebounced.watch(reloadProductByIdFx);
